import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './context/AuthContext';
import { apiGet, apiGetOwnershipXml, apiPost } from './api/client';
import LvSummaryAll from './components/LvSummaryAll';
import { usePdfLvSummary } from './hooks/usePdfLvSummary';
import { buildLvNumberPayload } from './utils/buildLvNumberPayload';
import { formatSavedPropertyLabel, isPdfBackedProperty } from './utils/propertyLabel';
import { fetchStoredLvPdf } from './utils/fetchStoredLvPdf';
import { useStoredLvPdfHydration } from './hooks/useStoredLvPdfHydration';
import { DOCUMENT_KEYS, loadDocumentDraft, saveDocumentDraft } from './utils/documentDrafts';

const LAST_KU_LV_KEY = 'realty-cuzk-last-ku-lv';

function RentOrderPage({ onBack, onSetupRequired, initialProperty, onSwitchToSaleFlatOrder, isAdmin = false }) {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const selectedSourceUserId = initialProperty?.user_id ? String(initialProperty.user_id) : '';

  // Step 1: KU + LV
  const [ku, setKu] = useState('');
  const [lv, setLv] = useState('');
  const [loadLoading, setLoadLoading] = useState(false);
  const [error, setError] = useState(null);

  // Step 2: LV loaded
  const [xml, setXml] = useState(null);
  const [summary, setSummary] = useState(null);
  const [floor, setFloor] = useState('');
  const [dispozice, setDispozice] = useState('');
  const [vymeraM2, setVymeraM2] = useState('');
  const [ulice, setUlice] = useState('');
  const [co, setCo] = useState('');
  const [obec, setObec] = useState('');
  const [psc, setPsc] = useState('');
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [gdprLoading, setGdprLoading] = useState(false);
  const [gdprError, setGdprError] = useState(null);
  const [pdfBase64, setPdfBase64] = useState('');
  const [, setPdfName] = useState('');

  const { pdfSummary } = usePdfLvSummary(pdfBase64, getToken);
  const autoLoadKeyRef = useRef('');

  useStoredLvPdfHydration(initialProperty, getToken, {
    setPdfBase64,
    setPdfName,
    setXml,
    setSummary,
    setError,
    setLoadLoading,
    translateError: () => t('rentOrder.errorFetch'),
    allowTargetUserId: isAdmin,
  });

  // Saved properties (LVs) for this agent
  const [savedProperties, setSavedProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');

  const kuTrim = (ku || '').trim();
  const lvTrim = (lv || '').trim();
  const canLoadLv = kuTrim.length > 0 && lvTrim.length > 0 && !loadLoading;

  const floorNum = floor === '' ? null : (parseInt(floor, 10) | 0);
  const hasXml = typeof xml === 'string' && xml.trim().length > 0;
  const hasSource = hasXml || !!pdfBase64;
  const canGenerate = hasXml && !generateLoading;
  const canGenerateGdpr = hasXml && !gdprLoading;

  // When opened from Properties page, prefill KU + LV
  useEffect(() => {
    if (initialProperty) {
      setKu(initialProperty.kod_ku || '');
      setLv(initialProperty.cislo_lv || '');
      if (initialProperty.pdfBase64) {
        setPdfBase64(String(initialProperty.pdfBase64));
        setPdfName(String(initialProperty.pdfName || 'LV.pdf'));
        setXml(null);
        setSummary(null);
      }
    }
  }, [initialProperty]);

  // Restore last KU/LV when opening without a pre-selected property
  useEffect(() => {
    if (initialProperty) return;
    try {
      const raw = sessionStorage.getItem(LAST_KU_LV_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const k = parsed?.ku != null ? String(parsed.ku).trim() : '';
      const l = parsed?.lv != null ? String(parsed.lv).trim() : '';
      if (k && l) {
        setKu(k);
        setLv(l);
      }
    } catch {
      /* ignore */
    }
  }, [initialProperty]);

  // Load saved properties once (for KU/LV picker)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiGet('/api/properties', getToken);
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && Array.isArray(data.properties)) {
          setSavedProperties(data.properties);
        }
      } catch {
        // Silently ignore; rent order can still work without saved list
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  const hydrateDraftForKuLv = useCallback(async (k, l) => {
    try {
      const draft = await loadDocumentDraft(
        {
          kod_ku: k,
          cislo_lv: l,
          document_key: DOCUMENT_KEYS.RENT_ORDER,
          user_id: selectedSourceUserId || undefined,
        },
        getToken,
      );
      const payload = draft?.payload;
      if (!payload || typeof payload !== 'object') return;
      setFloor(payload.floor == null ? '' : String(payload.floor));
      setDispozice(payload.dispozice == null ? '' : String(payload.dispozice));
      setVymeraM2(payload.vymeraM2 == null ? '' : String(payload.vymeraM2));
      setUlice(payload.ulice == null ? '' : String(payload.ulice));
      setCo(payload.co == null ? '' : String(payload.co));
      setObec(payload.obec == null ? '' : String(payload.obec));
      setPsc(payload.psc == null ? '' : String(payload.psc));
    } catch (draftErr) {
      console.warn('rentOrder: failed to load saved draft', draftErr.message);
    }
  }, [getToken, selectedSourceUserId]);

  const loadXmlForKuLv = useCallback(
    async (kRaw, lRaw) => {
      const k = String(kRaw ?? '').trim();
      const l = String(lRaw ?? '').trim();
      if (!k || !l) return;
      setError(null);
      setLoadLoading(true);
      try {
        const { res, data } = await apiGetOwnershipXml({
          KodKU: k,
          CisloLv: l,
          userId: selectedSourceUserId || undefined,
          getToken,
          confirmMessage: t('lvWorkspace.refreshWarning'),
          allowPaidFetch: true,
        });

        if (res.status === 403 && data.code === 'SETUP_REQUIRED' && onSetupRequired) {
          onSetupRequired();
          return;
        }
        if (!res.ok) {
          const msg = data.error || t('rentOrder.errorFetch');
          throw new Error(msg);
        }
        if (!data.xml || typeof data.xml !== 'string') {
          throw new Error(t('rentOrder.errorNoXml'));
        }
        setXml(data.xml);
        setSummary(data.summary || null);
        setFloor('');
        setDispozice('');
        setVymeraM2('');
        setUlice('');
        setCo('');
        setObec('');
        setPsc('');
        setGenerateError(null);
        await hydrateDraftForKuLv(k, l);

        try {
          const listRes = await apiGet('/api/properties', getToken);
          const listData = await listRes.json().catch(() => ({}));
          if (listRes.ok && Array.isArray(listData.properties)) {
            setSavedProperties(listData.properties);
          }
        } catch {
          /* non-critical */
        }
      } catch (err) {
        let message = err.message || t('rentOrder.errorUnknown');
        if (err.name === 'TypeError' && (err.message === 'Failed to fetch' || err.message?.includes?.('NetworkError'))) {
          message = t('rentOrder.errorNetwork');
        }
        setError(message);
      } finally {
        setLoadLoading(false);
      }
    },
    [getToken, hydrateDraftForKuLv, onSetupRequired, selectedSourceUserId, t],
  );

  useEffect(() => {
    if (!initialProperty?.kod_ku || !initialProperty?.cislo_lv) {
      autoLoadKeyRef.current = '';
      return;
    }
    const k = String(initialProperty.kod_ku).trim();
    const l = String(initialProperty.cislo_lv).trim();
    if (!k || !l) return;
    const key = `${k}-${l}`;
    if (autoLoadKeyRef.current === key) return;
    autoLoadKeyRef.current = key;
    setKu(k);
    setLv(l);
    if (initialProperty.pdfBase64) return;
    const path = initialProperty.lv_pdf_storage_path || initialProperty.lvPdfStoragePath;
    if (path) return;
    loadXmlForKuLv(k, l);
  }, [initialProperty, loadXmlForKuLv]);

  const handleLoadLv = async (e) => {
    e.preventDefault();
    if (!isAdmin || !canLoadLv) return;
    await loadXmlForKuLv(kuTrim, lvTrim);
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!canGenerate) return;
    setGenerateError(null);
    setGenerateLoading(true);
    try {
      const userInput = {
        floor: floorNum,
        dispozice: (dispozice || '').trim(),
        vymeraM2: (vymeraM2 || '').trim(),
        ulice: (ulice || '').trim(),
        co: (co || '').trim(),
        obec: (obec || '').trim(),
        psc: (psc || '').trim(),
      };
      try {
        await saveDocumentDraft(
          {
            kod_ku: kuTrim,
            cislo_lv: lvTrim,
            document_key: DOCUMENT_KEYS.RENT_ORDER,
            user_id: selectedSourceUserId || undefined,
          },
          userInput,
          getToken,
        );
      } catch (draftErr) {
        console.warn('rentOrder: failed to save draft', draftErr.message);
      }
      const payload = { xml, userInput };
      const genRes = await apiPost('/api/generate-rent-order', payload, getToken);

      if (genRes.status === 403) {
        const genData = await genRes.json().catch(() => ({}));
        if (genData.code === 'SETUP_REQUIRED' && onSetupRequired) {
          onSetupRequired();
          return;
        }
        throw new Error(genData.error || t('rentOrder.errorGenerate'));
      }
      if (!genRes.ok) {
        const genData = await genRes.json().catch(() => ({}));
        throw new Error(genData.error || t('rentOrder.errorGenerate'));
      }

      const blob = await genRes.blob();
      const disposition = genRes.headers.get('Content-Disposition');
      const match = disposition && disposition.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1].trim() : `RENT_1_vyplneno-${kuTrim}-${lvTrim}.docx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      let message = err.message || t('rentOrder.errorUnknown');
      if (err.name === 'TypeError' && (err.message === 'Failed to fetch' || err.message?.includes?.('NetworkError'))) {
        message = t('rentOrder.errorNetwork');
      }
      setGenerateError(message);
    } finally {
      setGenerateLoading(false);
    }
  };

  const handleGenerateGdpr = async (e) => {
    e.preventDefault();
    if (!canGenerateGdpr) return;
    setGdprError(null);
    setGdprLoading(true);
    try {
      const lvExtra = buildLvNumberPayload({ summary, pdfSummary, initialProperty, lvTrim });
      const payload = { xml, ...lvExtra };
      const genRes = await apiPost('/api/generate-gdpr', payload, getToken);

      if (genRes.status === 404) {
        const genData = await genRes.json().catch(() => ({}));
        if (genData.code === 'GDPR_TEMPLATE_MISSING') {
          setGdprError(genData.error || t('rentOrder.gdprTemplateMissing'));
          return;
        }
      }
      if (!genRes.ok) {
        const genData = await genRes.json().catch(() => ({}));
        throw new Error(genData.error || t('rentOrder.errorGenerate'));
      }

      const blob = await genRes.blob();
      const disposition = genRes.headers.get('Content-Disposition');
      const match = disposition && disposition.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1].trim() : `GDPR_vyplneno-${kuTrim}-${lvTrim}.docx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      let message = err.message || t('rentOrder.errorUnknown');
      if (err.name === 'TypeError' && (err.message === 'Failed to fetch' || err.message?.includes?.('NetworkError'))) {
        message = t('rentOrder.errorNetwork');
      }
      setGdprError(message);
    } finally {
      setGdprLoading(false);
    }
  };

  // PDF upload is intentionally disabled in XML-first mode.

  const step = hasSource ? 'details' : 'enter';
  const inputClass =
    'w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/50 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-300 transition-shadow';
  const labelClass = 'block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5';

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-3 sm:px-4 py-6 sm:py-8 w-full max-w-full min-w-0">
      <div className="w-full max-w-md min-w-0">
        <div className="rounded-2xl border border-slate-200/80 bg-white shadow-lg shadow-slate-200/50 p-4 sm:p-8">
          <h1 className="text-xl font-semibold text-slate-800 tracking-tight mb-1">
            {t('rentOrder.landingTitle')}
          </h1>
          <p className="text-slate-500 text-sm mb-8">
            {t('rentOrder.landingSubtitle')}
          </p>

          {step === 'enter' && (
            <form onSubmit={handleLoadLv} className="space-y-5">
              {savedProperties.length > 0 && (
                <div>
                  <label className={labelClass}>{t('rentOrder.savedPropertiesLabel')}</label>
                  <select
                    className={inputClass}
                    value={selectedPropertyId}
                    onChange={async (e) => {
                      const id = e.target.value;
                      setSelectedPropertyId(id);
                      const prop = savedProperties.find((p) => String(p.id) === id);
                      if (prop) {
                        const kk = String(prop.kod_ku || '').trim();
                        const ll = String(prop.cislo_lv || '').trim();
                        setKu(kk);
                        setLv(ll);
                        if (isPdfBackedProperty(prop)) {
                          setError(null);
                          setLoadLoading(true);
                          try {
                            const { pdfBase64: b64, pdfName: name } = await fetchStoredLvPdf(getToken, {
                              kod_ku: kk,
                              cislo_lv: ll,
                              user_id: isAdmin && prop.user_id ? prop.user_id : undefined,
                            });
                            setPdfBase64(b64);
                            setPdfName(name || 'LV.pdf');
                            setXml(null);
                            setSummary(null);
                          } catch (err) {
                            setError(err.message || t('rentOrder.errorFetch'));
                          } finally {
                            setLoadLoading(false);
                          }
                        } else if (!isAdmin && kk && ll) {
                          loadXmlForKuLv(kk, ll);
                        }
                      }
                    }}
                  >
                    <option value="">{t('rentOrder.savedPropertiesPlaceholder')}</option>
                    {savedProperties.map((p) => (
                      <option key={p.id} value={p.id}>
                        {formatSavedPropertyLabel(p, t)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="rent-ku" className={labelClass}>
                    {t('rentOrder.kuLabel')}
                  </label>
                  <input
                    id="rent-ku"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={ku}
                    onChange={(e) => setKu(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder={t('rentOrder.kuPlaceholder')}
                    className={inputClass}
                    aria-invalid={error ? 'true' : undefined}
                  />
                </div>
                <div>
                  <label htmlFor="rent-lv" className={labelClass}>
                    {t('rentOrder.lvLabel')}
                  </label>
                  <input
                    id="rent-lv"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={lv}
                    onChange={(e) => setLv(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder={t('rentOrder.lvPlaceholder')}
                    className={inputClass}
                    aria-invalid={error ? 'true' : undefined}
                  />
                </div>
              </div>

              {error && (
                <div className="rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-700" role="alert">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={!canLoadLv}
                className="w-full py-3.5 px-4 rounded-xl bg-slate-800 text-white font-medium text-sm tracking-wide hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {loadLoading ? (
                  <>
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden />
                    {t('rentOrder.loadingLv')}
                  </>
                ) : (
                  t('rentOrder.loadLvButton')
                )}
              </button>
              {/* PDF upload UX temporarily disabled (XML-first mode)
              {isAdmin && (
                <div className="pt-2 border-t border-slate-100">
                  <label className={labelClass}>{t('common.adminPdfUploadLabel')}</label>
                  <input type="file" accept="application/pdf" onChange={handlePdfUpload} className="block w-full text-sm text-slate-600" />
                  {pdfName && <p className="text-xs text-slate-500 mt-1">{t('common.selectedPdf')}: {pdfName}</p>}
                </div>
              )}
              */}
            </form>
          )}

          {step === 'details' && (
            <div className="space-y-6">
              {/* LV summary – all parsed data */}
              <div>
                <h2 className="text-sm font-semibold text-slate-700 mb-1">{t('rentOrder.fromLvTitle')}</h2>
                <p className="text-xs text-slate-500 mb-3">{t('rentOrder.fromLvIntro')}</p>
                <div className="rounded-xl bg-slate-50 border border-slate-100 p-4">
                  {summary ? (
                    <LvSummaryAll summary={summary} />
                  ) : (
                    <p className="text-sm text-slate-500">LV data loaded. Summary not available.</p>
                  )}
                </div>
              </div>

              {/* Form: floor, dispozice, vymeraM2, ulice, obec, psc */}
              <div>
                <h2 className="text-sm font-semibold text-slate-700 mb-1">{t('rentOrder.pleaseProvideTitle')}</h2>
                <p className="text-xs text-slate-500 mb-3">{t('rentOrder.pleaseProvideIntro')}</p>
                <form onSubmit={handleGenerate} className="space-y-4">
                  <div>
                    <label htmlFor="rent-floor" className={labelClass}>
                      {t('rentOrder.floorLabel')}
                    </label>
                    <input
                      id="rent-floor"
                      type="number"
                      min={0}
                      max={99}
                      value={floor}
                      onChange={(e) => setFloor(e.target.value.replace(/\D/g, '').slice(0, 3))}
                      placeholder="e.g. 3"
                      className={inputClass}
                    />
                    <p className="text-xs text-slate-400 mt-1">{t('rentOrder.floorHint')}</p>
                  </div>
                  <div>
                    <label htmlFor="rent-dispozice" className={labelClass}>
                      {t('rentOrder.dispoziceLabel')}
                    </label>
                    <input
                      id="rent-dispozice"
                      type="text"
                      value={dispozice}
                      onChange={(e) => setDispozice(e.target.value)}
                      placeholder={t('rentOrder.dispozicePlaceholder')}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="rent-vymera" className={labelClass}>
                      {t('rentOrder.vymeraM2Label')}
                    </label>
                    <p className="text-xs text-slate-400 mb-1.5">{t('docFormHints.vymeraM2')}</p>
                    <input
                      id="rent-vymera"
                      type="text"
                      inputMode="decimal"
                      value={vymeraM2}
                      onChange={(e) => setVymeraM2(e.target.value)}
                      placeholder={t('rentOrder.vymeraM2Placeholder')}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="rent-ulice" className={labelClass}>
                      {t('rentOrder.uliceLabel')}
                    </label>
                    <p className="text-xs text-slate-400 mb-1.5">{t('docFormHints.street')}</p>
                    <input
                      id="rent-ulice"
                      type="text"
                      value={ulice}
                      onChange={(e) => setUlice(e.target.value)}
                      placeholder={t('rentOrder.ulicePlaceholder')}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="rent-co" className={labelClass}>
                      {t('rentOrder.coLabel')}
                    </label>
                    <input
                      id="rent-co"
                      type="text"
                      value={co}
                      onChange={(e) => setCo(e.target.value)}
                      placeholder={t('rentOrder.coPlaceholder')}
                      className={inputClass}
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="rent-obec" className={labelClass}>
                        {t('rentOrder.obecLabel')}
                      </label>
                      <p className="text-xs text-slate-400 mb-1.5">{t('docFormHints.obec')}</p>
                      <input
                        id="rent-obec"
                        type="text"
                        value={obec}
                        onChange={(e) => setObec(e.target.value)}
                        placeholder={t('rentOrder.obecPlaceholder')}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label htmlFor="rent-psc" className={labelClass}>
                        {t('rentOrder.pscLabel')}
                      </label>
                      <p className="text-xs text-slate-400 mb-1.5">{t('docFormHints.psc')}</p>
                      <input
                        id="rent-psc"
                        type="text"
                        value={psc}
                        onChange={(e) => setPsc(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder={t('rentOrder.pscPlaceholder')}
                        className={inputClass}
                      />
                    </div>
                  </div>

                  {generateError && (
                    <div className="rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-700" role="alert">
                      {generateError}
                    </div>
                  )}
                  {gdprError && (
                    <div className="rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-700" role="alert">
                      {gdprError}
                    </div>
                  )}

                  <div className="flex flex-col gap-2">
                    <button
                      type="submit"
                      disabled={!canGenerate}
                      className="w-full py-3.5 px-4 rounded-xl bg-slate-800 text-white font-medium text-sm tracking-wide hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                    >
                      {generateLoading ? (
                        <>
                          <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden />
                          {t('rentOrder.generating')}
                        </>
                      ) : (
                        t('rentOrder.generateDocAction')
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={handleGenerateGdpr}
                      disabled={!canGenerateGdpr}
                      className="w-full py-2.5 px-4 rounded-xl border border-slate-200 text-slate-600 text-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                    >
                      {gdprLoading ? (
                        <>
                          <span className="inline-block w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" aria-hidden />
                          {t('rentOrder.generatingGdpr')}
                        </>
                      ) : (
                        t('rentOrder.generateGdprAction')
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={onBack}
                      className="w-full py-2.5 px-4 rounded-xl border border-slate-200 text-slate-600 text-sm hover:bg-slate-50 transition-colors"
                    >
                      {t('rentOrder.backMainAction')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {step === 'enter' && (
            <p className="mt-6 text-xs text-slate-400 text-center">
              {t('rentOrder.landingHint')}{' '}
              <a
                href="https://nahlizenidokn.cuzk.gov.cz"
                target="_blank"
                rel="noreferrer noopener"
                className="underline"
              >
                nahlizenidokn.cuzk.gov.cz
              </a>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default RentOrderPage;
